import { useCurrentTheme } from "@/providers/ThemeProvider";
import DOMPurify from "dompurify";
import { useEffect, useMemo, useRef } from "react";

const PURIFY_CONFIG = {
  ALLOW_DATA_ATTR: false,
  ADD_TAGS: ["base"],
  WHOLE_DOCUMENT: true,
  SANITIZE_DOM: true,
  FORBID_TAGS: ["script", "style", "link", "form", "input", "iframe", "meta"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
};

interface SanitizedHtmlProps {
  htmlContent: string;
  loadRemoteImages?: boolean;
}

export default function SanitizedHtml({
  htmlContent,
  loadRemoteImages = true,
}: SanitizedHtmlProps) {
  const currentTheme = useCurrentTheme();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Generate sanitized HTML content for srcdoc (Safari-compatible)
  const srcdocContent = useMemo(() => {
    const sanitizedHtml = DOMPurify.sanitize(htmlContent, {
      ...PURIFY_CONFIG,
    });

    const imgSrc = loadRemoteImages
      ? "img-src 'self' https: data:;"
      : "img-src 'self' data:;";

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <base target="_blank">
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <meta http-equiv="Content-Security-Policy"
                content="default-src 'self' https:;
                         script-src 'none';
                         ${imgSrc}
                         style-src 'self' 'unsafe-inline';">
          <style>
            body {
              margin: 0;
              font-family: ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
              font-size: 0.875rem;
              line-height: 1.25rem;
              color: #000;
              height: 100%;
              overflow-y: hidden;
              overflow-x: hidden;
              word-wrap: break-word;
              word-break: break-word;
              ${
                currentTheme === "dark"
                  ? `
              filter: invert(1) hue-rotate(180deg);
              `
                  : ""
              }
            }
            img, video, iframe {
              ${
                currentTheme === "dark"
                  ? `
              filter: invert(1) hue-rotate(180deg);
              `
                  : ""
              }
            }
            html {
              height: 100%;
            }
          </style>
        </head>
        <body>${sanitizedHtml}</body>
      </html>
    `;
  }, [htmlContent, currentTheme, loadRemoteImages]);

  const updateIframeHeight = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const iframeDoc = iframe.contentDocument ?? iframe.contentWindow?.document;
    if (!iframeDoc) return;

    const height = iframeDoc.documentElement.scrollHeight;

    iframe.style.height = `${height}px`;
  };

  useEffect(() => {
    if (!iframeRef.current) return;

    const iframe = iframeRef.current;

    iframe.style.height = "0px";

    // Handle iframe load event
    const handleLoad = () => {
      const iframeWindow = iframe.contentWindow;
      if (!iframeWindow) return;

      const iframeDoc = iframeWindow.document;
      if (!iframeDoc) return;

      // Setup image load listeners
      const images = iframeDoc.getElementsByTagName("img");
      Array.from(images).forEach((img) => {
        img.addEventListener("load", updateIframeHeight);
        img.addEventListener("error", updateIframeHeight);
      });

      // Setup link click handler
      const handleClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const link = target.closest("a");
        if (link) {
          e.preventDefault();
          const href = link.getAttribute("href");
          if (href) {
            window.open(href, "_blank", "noopener,noreferrer");
          }
        }
      };

      iframeDoc.addEventListener("click", handleClick);

      // Initial height update
      updateIframeHeight();

      // Store cleanup function
      iframe.dataset.cleanupRegistered = "true";
    };

    iframe.addEventListener("load", handleLoad);

    return () => {
      iframe.removeEventListener("load", handleLoad);

      // Cleanup iframe document listeners if they were registered
      if (iframe.dataset.cleanupRegistered) {
        const iframeDoc = iframe.contentDocument;
        if (iframeDoc) {
          const images = iframeDoc.getElementsByTagName("img");
          Array.from(images).forEach((img) => {
            img.removeEventListener("load", updateIframeHeight);
            img.removeEventListener("error", updateIframeHeight);
          });
        }
      }
    };
  }, [srcdocContent]);

  return (
    <iframe
      ref={iframeRef}
      className="w-full h-full border-none"
      sandbox="allow-same-origin"
      srcDoc={srcdocContent}
    />
  );
}
